/**
 * SimpleAccessibilityContext.js
 *
 * Simplified accessibility management for LeadifyAI
 * Focuses on the 5 most essential accessibility features:
 * - Font size (small/normal/large)
 * - High contrast (on/off)
 * - Highlight links (on/off)
 * - Disable animations (on/off)
 * - Text spacing (normal/increased)
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { createTheme, ThemeProvider } from "@mui/material/styles";

// Storage key for preferences
const STORAGE_KEY = "leadifyai_accessibility_simple";

// Font size multipliers - simplified to 3 options (less extreme)
const FONT_SIZES = {
  small: 0.95, // 95% of normal (subtle reduction)
  normal: 1, // 100% (default)
  large: 1.10, // 110% of normal (gentle increase)
};

// Initial state - all features off by default
const initialState = {
  fontSize: "normal",
  highContrast: false,
  highlightLinks: false,
  disableAnimations: false,
  increasedSpacing: false,
};

// Create context
const SimpleAccessibilityContext = createContext(null);

// Custom hook for using accessibility context
export const useAccessibility = () => {
  const context = useContext(SimpleAccessibilityContext);
  if (!context) {
    throw new Error(
      "useAccessibility must be used within SimpleAccessibilityProvider"
    );
  }
  return context;
};

// Provider component
export const SimpleAccessibilityProvider = ({ children, baseTheme }) => {
  const [settings, setSettings] = useState(initialState);

  // Load preferences from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const preferences = JSON.parse(stored);
        const merged = { ...initialState, ...preferences };
        // Skip the update when the stored prefs equal the defaults (the
        // common case): the unconditional setSettings forced a SECOND
        // full-tree render + createTheme on every single page load.
        const differs = Object.keys(merged).some(
          (key) => merged[key] !== initialState[key]
        );
        if (differs) {
          setSettings(merged);
        }
      }
    } catch (error) {
      console.error("Failed to load accessibility preferences:", error);
    }
  }, []);

  // Save preferences whenever settings change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    } catch (error) {
      console.error("Failed to save accessibility preferences:", error);
    }
  }, [settings]);

  // Apply CSS classes and variables to document body
  useEffect(() => {
    const root = document.documentElement;
    const body = document.body;

    // Remove all accessibility classes first
    body.className = body.className
      .split(" ")
      .filter((cls) => !cls.startsWith("a11y-"))
      .join(" ");

    // Font size
    const fontMultiplier = FONT_SIZES[settings.fontSize];
    root.style.setProperty("--a11y-font-size", fontMultiplier);
    body.classList.add(`a11y-font-${settings.fontSize}`);

    // High contrast
    if (settings.highContrast) {
      body.classList.add("a11y-high-contrast");
    }

    // Highlight links
    if (settings.highlightLinks) {
      body.classList.add("a11y-highlight-links");
    }

    // Disable animations
    if (settings.disableAnimations) {
      body.classList.add("a11y-no-animations");
      root.style.setProperty("--a11y-animation-duration", "0s");
      root.style.setProperty("--a11y-transition-duration", "0s");
    } else {
      root.style.setProperty("--a11y-animation-duration", "initial");
      root.style.setProperty("--a11y-transition-duration", "initial");
    }

    // Text spacing
    if (settings.increasedSpacing) {
      body.classList.add("a11y-increased-spacing");
      root.style.setProperty("--a11y-line-height", "1.8");
      root.style.setProperty("--a11y-letter-spacing", "0.05em");
      root.style.setProperty("--a11y-word-spacing", "0.1em");
      root.style.setProperty("--a11y-paragraph-spacing", "2em");
    } else {
      root.style.setProperty("--a11y-line-height", "1.5");
      root.style.setProperty("--a11y-letter-spacing", "normal");
      root.style.setProperty("--a11y-word-spacing", "normal");
      root.style.setProperty("--a11y-paragraph-spacing", "1em");
    }

    // Cleanup function
    return () => {
      // Remove all accessibility classes on unmount
      body.className = body.className
        .split(" ")
        .filter((cls) => !cls.startsWith("a11y-"))
        .join(" ");
    };
  }, [settings]);

  // Generate Material-UI theme with accessibility settings
  const accessibleTheme = React.useMemo(() => {
    const defaultTheme = baseTheme || createTheme();
    const fontMultiplier = FONT_SIZES[settings.fontSize];

    // Define colors based on contrast setting
    const colors = settings.highContrast
      ? {
          background: "#FFFFFF",
          paper: "#FFFFFF",
          text: "#000000",
          textSecondary: "#000000",
          primary: "#005A52",
          primaryDark: "#003D36",
          error: "#B71C1C",
          success: "#1B5E20",
          divider: "rgba(0, 0, 0, 0.38)",
        }
      : {
          background: "#FFFFFF",
          paper: "#F5F7FA",
          text: "#333333",
          textSecondary: "#666666",
          primary: "#40E0D0",
          primaryDark: "#00C9B0",
          error: "#f44336",
          success: "#4caf50",
          divider: "rgba(0, 0, 0, 0.12)",
        };

    return createTheme({
      ...defaultTheme,
      palette: {
        ...defaultTheme.palette,
        mode: "light",
        background: {
          default: colors.background,
          paper: colors.paper,
        },
        text: {
          primary: colors.text,
          secondary: colors.textSecondary,
        },
        primary: {
          main: colors.primary,
          dark: colors.primaryDark,
          contrastText: "#FFFFFF",
        },
        error: {
          main: colors.error,
        },
        success: {
          main: colors.success,
        },
        divider: colors.divider,
      },
      typography: {
        ...defaultTheme.typography,
        fontSize: 14 * fontMultiplier,
        h1: {
          ...defaultTheme.typography.h1,
          fontSize: `${2.5 * fontMultiplier}rem`,
        },
        h2: {
          ...defaultTheme.typography.h2,
          fontSize: `${2 * fontMultiplier}rem`,
        },
        h3: {
          ...defaultTheme.typography.h3,
          fontSize: `${1.75 * fontMultiplier}rem`,
        },
        h4: {
          ...defaultTheme.typography.h4,
          fontSize: `${1.5 * fontMultiplier}rem`,
        },
        h5: {
          ...defaultTheme.typography.h5,
          fontSize: `${1.25 * fontMultiplier}rem`,
        },
        h6: {
          ...defaultTheme.typography.h6,
          fontSize: `${1.1 * fontMultiplier}rem`,
        },
        body1: {
          ...defaultTheme.typography.body1,
          fontSize: `${1 * fontMultiplier}rem`,
          lineHeight: settings.increasedSpacing ? 1.8 : 1.5,
        },
        body2: {
          ...defaultTheme.typography.body2,
          fontSize: `${0.875 * fontMultiplier}rem`,
          lineHeight: settings.increasedSpacing ? 1.8 : 1.5,
        },
      },
      components: {
        ...defaultTheme.components,
        MuiButton: {
          styleOverrides: {
            root: {
              transition: settings.disableAnimations ? "none" : undefined,
            },
          },
        },
        MuiInputBase: {
          styleOverrides: {
            root: {
              "&.Mui-focused": {
                outline: settings.highContrast
                  ? "3px solid #000000"
                  : "2px solid #0066CC",
                outlineOffset: "2px",
              },
            },
          },
        },
      },
    });
  }, [settings, baseTheme]);

  // Action functions
  const setFontSize = useCallback((size) => {
    if (["small", "normal", "large"].includes(size)) {
      setSettings((prev) => ({ ...prev, fontSize: size }));
    }
  }, []);

  const toggleHighContrast = useCallback(() => {
    setSettings((prev) => ({ ...prev, highContrast: !prev.highContrast }));
  }, []);

  const toggleHighlightLinks = useCallback(() => {
    setSettings((prev) => ({ ...prev, highlightLinks: !prev.highlightLinks }));
  }, []);

  const toggleAnimations = useCallback(() => {
    setSettings((prev) => ({
      ...prev,
      disableAnimations: !prev.disableAnimations,
    }));
  }, []);

  const toggleTextSpacing = useCallback(() => {
    setSettings((prev) => ({
      ...prev,
      increasedSpacing: !prev.increasedSpacing,
    }));
  }, []);

  const resetAll = useCallback(() => {
    setSettings(initialState);
  }, []);

  // Announce changes to screen readers
  const announceChange = useCallback((message) => {
    const announcement = document.createElement("div");
    announcement.setAttribute("role", "alert");
    announcement.setAttribute("aria-live", "polite");
    announcement.style.position = "absolute";
    announcement.style.left = "-10000px";
    announcement.textContent = message;
    document.body.appendChild(announcement);
    setTimeout(() => document.body.removeChild(announcement), 1000);
  }, []);

  // Context value
  const value = {
    // Current settings
    ...settings,

    // Actions
    setFontSize,
    toggleHighContrast,
    toggleHighlightLinks,
    toggleAnimations,
    toggleTextSpacing,
    resetAll,

    // Utilities
    theme: accessibleTheme,
    announceChange,

    // For compatibility with existing code
    animationsEnabled: !settings.disableAnimations,
  };

  return (
    // ThemeProvider must wrap the context provider so that MUI components
    // deeper in the tree receive the composed theme (base + a11y overrides).
    // accessibleTheme recomputes via useMemo when settings change, so MUI
    // components re-render with updated font sizes / high-contrast colours
    // automatically — no manual theme.useTheme() calls needed in components.
    <ThemeProvider theme={accessibleTheme}>
      <SimpleAccessibilityContext.Provider value={value}>
        {children}
      </SimpleAccessibilityContext.Provider>
    </ThemeProvider>
  );
};

// Helper component for screen reader only text
export const ScreenReaderOnly = ({ children }) => {
  return (
    <span
      style={{
        position: "absolute",
        left: "-10000px",
        width: "1px",
        height: "1px",
        overflow: "hidden",
      }}
    >
      {children}
    </span>
  );
};

export default SimpleAccessibilityProvider;
